import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HeadphonesIcon, Loader2, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import { chatAPI, Message } from '../services/chatService';
import { useSocket } from '../hooks/useSocket';
import { useAuthStore } from '../store/authStore';

/**
 * Klantenservice-chat. Woonde eerder in het Mijn Belagom-menu; hangt nu in de
 * zwevende support-widget zodat de professional overal direct kan aankloppen.
 */
export const SupportChatPanel: React.FC = () => {
  const { t } = useTranslation('belagom');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get or create the support conversation
  const { data: supportConv, isLoading: isStarting } = useQuery({
    queryKey: ['belagom-support-conversation'],
    queryFn: chatAPI.getOrCreateBelagomSupportConversation,
  });

  useEffect(() => {
    if (supportConv?.id) setConversationId(supportConv.id);
  }, [supportConv]);

  // Load messages when conversation is known
  const { isLoading: isLoadingMessages } = useQuery({
    queryKey: ['belagom-support-messages', conversationId],
    queryFn: () => chatAPI.getMessages(conversationId!),
    enabled: !!conversationId,
  });

  const fetchedMessages = useQuery({
    queryKey: ['belagom-support-messages', conversationId],
    queryFn: () => chatAPI.getMessages(conversationId!),
    enabled: !!conversationId,
  }).data;

  useEffect(() => {
    if (fetchedMessages) setMessages(fetchedMessages);
  }, [fetchedMessages]);

  // Real-time socket
  const handleNewMessage = useCallback((msg: Message) => {
    setMessages((prev) => {
      if (prev.find((m) => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);

  const { startTyping, stopTyping } = useSocket({
    conversationId: conversationId ?? undefined,
    onNewMessage: handleNewMessage,
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send via REST (socket send is handled server-side via socket events)
  const sendMutation = useMutation({
    mutationFn: (content: string) => chatAPI.sendMessage(conversationId!, content),
    onSuccess: (msg) => {
      setMessages((prev) => (prev.find((m) => m.id === msg.id) ? prev : [...prev, msg]));
      queryClient.invalidateQueries({ queryKey: ['belagom-support-messages', conversationId] });
    },
    onError: () => toast.error(t('toast.messageFailed')),
  });

  const handleSend = () => {
    const content = newMessage.trim();
    if (!content || !conversationId) return;
    setNewMessage('');
    stopTyping(conversationId);
    sendMutation.mutate(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    if (conversationId) {
      startTyping(conversationId);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => stopTyping(conversationId), 2000);
    }
  };

  const formatTime = (dateStr: string) =>
    formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: nl });

  if (isStarting) {
    return (
      <div className="flex items-center justify-center min-h-[400px] gap-3 text-gray-500">
        <Loader2 size={20} className="animate-spin" />
        <span>{t('loading.support')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-[#0B1F3A] text-white">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <HeadphonesIcon size={18} />
        </div>
        <div>
          <p className="font-semibold text-sm">{t('section.customerService')}</p>
          <p className="text-xs text-white/70">{t('info.supportTagline')}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50">
        {isLoadingMessages && (
          <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">{t('loading.messages')}</span>
          </div>
        )}

        {!isLoadingMessages && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-3">
              <HeadphonesIcon size={24} className="text-[#0B1F3A]" />
            </div>
            <p className="text-sm font-medium text-gray-700">{t('label.askQuestion')}</p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs">
              {t('info.supportHint')}
            </p>
          </div>
        )}

        {messages.map((msg) => {
          const isOwn = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] ${isOwn ? '' : 'flex items-end gap-2'}`}>
                {!isOwn && (
                  <div className="w-7 h-7 rounded-full bg-[#0B1F3A] flex items-center justify-center flex-shrink-0 mb-1">
                    <HeadphonesIcon size={13} className="text-white" />
                  </div>
                )}
                <div>
                  <div
                    className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      isOwn
                        ? 'bg-[#0B1F3A] text-white rounded-br-sm'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                  <p className={`text-[10px] text-gray-400 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 bg-white border-t border-gray-100">
        <div className="flex items-end gap-2">
          <textarea
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('placeholder.supportQuestion')}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] max-h-32 overflow-y-auto"
            style={{ minHeight: '42px' }}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sendMutation.isPending}
            className="w-10 h-10 rounded-xl bg-[#0B1F3A] text-white flex items-center justify-center hover:bg-[#1A3459] transition-colors disabled:opacity-40 flex-shrink-0"
          >
            {sendMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">{t('info.sendKeyboardHint')}</p>
      </div>
    </div>
  );
};
